"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Icon from "../atoms/Icon";
import type { TabName } from "../types";

const tabRoutes: Record<TabName, string> = {
  Dashboard: "/dashboard",
  Membership: "/membership",
  Visits: "/visits",
  Profile: "/profile",
};

/** DOM id used to link the hamburger button's `aria-controls` to this drawer. */
const DRAWER_ID = "mobile-nav-drawer";

interface MobileDrawerProps {
  tabs: TabName[];
  activeTab: TabName;
  onClose: () => void;
  onLogoutRequest: () => void;
}

export { DRAWER_ID };

/**
 * @component MobileDrawer
 * @description Full-screen mobile navigation drawer with tab navigation.
 * Automatically focuses the close button on mount and dismisses on Escape.
 * @param tabs - Ordered list of tab names to render as navigation items.
 * @param activeTab - Currently selected tab.
 * @param onTabChange - Callback fired when the user selects a tab.
 * @param onClose - Callback to close the drawer (also triggered by pressing Escape).
 * @param onLogoutRequest - Callback fired when the user requests logout from mobile navigation.
 * @returns A full-screen overlay dialog with navigation buttons and a close control.
 */
export default function MobileDrawer({
  tabs,
  activeTab,
  onClose,
  onLogoutRequest,
}: MobileDrawerProps) {
  const router = useRouter();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      id={DRAWER_ID}
      className="fixed inset-0 z-50 bg-white flex flex-col lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      {/* Drawer Header */}
      <div className="flex items-center justify-between px-6 pt-6">
        <Icon
          name="welcome"
          className="w-16 h-8"
          alt="Grounds for Sculpture logo"
          decorative={false}
        />
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="p-2"
          aria-label="Close menu"
        >
          <svg
            className="w-7 h-7 text-black"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Drawer Nav Items */}
      <nav
        className="flex flex-col gap-2 px-6 mt-8 flex-1"
        aria-label="Main navigation"
      >
        {tabs.map((tabName) => (
          <button
            key={tabName}
            role="tab"
            aria-selected={activeTab === tabName}
            aria-controls={`tabpanel-${tabName}`}
            onClick={() => { router.push(tabRoutes[tabName]); onClose(); }}
            className={`text-left text-title py-3 ${
              activeTab === tabName
                ? "text-charcoal border-l-[3px] border-accent-green pl-4"
                : "text-stone pl-[19px]"
            }`}
          >
            {tabName}
          </button>
        ))}
      </nav>

      {/* Drawer Log Out */}
      <div className="px-6 pb-8">
        <button
          type="button"
          onClick={() => {
            onClose();
            onLogoutRequest();
          }}
          className="text-title text-stone"
        >
          Log Out
        </button>
      </div>
    </div>
  );
}
